import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faSpinner,
    faExclamationTriangle,
    faInfoCircle,
    faDatabase,
    faMemory,
} from '@fortawesome/free-solid-svg-icons';
import adminSecurityService from '../../../helpers/adminSecurityService';

const IPStatsModal = ({ ip, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchIPStats();
    }, [ip]);

    const fetchIPStats = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await adminSecurityService.getIPStats(ip);
            setStats(data);
        } catch (err) {
            console.error('Error fetching IP stats:', err);
            setError(err.message || 'Error al cargar estadísticas de la IP');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                    <div>
                        <h3 className="text-xl font-semibold text-gray-900">Estadísticas de IP</h3>
                        <p className="text-sm text-gray-600 mt-1 font-mono">{ip}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <FontAwesomeIcon icon={faTimes} className="text-xl" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl text-gray-400" />
                        </div>
                    ) : error ? (
                        <div className="bg-red-50 border border-red-200 rounded-md p-4">
                            <div className="flex items-start gap-2">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mt-0.5" />
                                <div>
                                    <h5 className="font-semibold text-red-900 mb-1">Error</h5>
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                            </div>
                        </div>
                    ) : stats ? (
                        <div className="space-y-6">
                            {/* Estado General */}
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h4 className="font-semibold text-gray-900 mb-3">Estado General</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm text-gray-600">Bloqueada en BD</p>
                                        <p className="text-lg font-medium text-gray-900">
                                            {stats.blockedInDB ? (
                                                <span className="text-red-600">Sí</span>
                                            ) : (
                                                <span className="text-green-600">No</span>
                                            )}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">Bloqueada en Memoria</p>
                                        <p className="text-lg font-medium text-gray-900">
                                            {stats.blockedInMemory ? (
                                                <span className="text-red-600">Sí</span>
                                            ) : (
                                                <span className="text-green-600">No</span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Información de Bloqueo en BD */}
                            {stats.blockedIP && (
                                <div className="border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <FontAwesomeIcon icon={faDatabase} className="text-blue-600" />
                                        <h4 className="font-semibold text-gray-900">Bloqueo en Base de Datos</h4>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Razón:</span>
                                            <span className="text-gray-900 font-medium">{stats.blockedIP.reason}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Tipo:</span>
                                            <span className="text-gray-900 font-medium">
                                                {stats.blockedIP.isPermanent ? (
                                                    <span className="text-red-600">Permanente</span>
                                                ) : (
                                                    <span className="text-yellow-600">Temporal</span>
                                                )}
                                            </span>
                                        </div>
                                        {stats.blockedIP.blockedUntil && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Bloqueado hasta:</span>
                                                <span className="text-gray-900 font-medium">
                                                    {new Date(stats.blockedIP.blockedUntil).toLocaleString('es-ES')}
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Intentos bloqueados:</span>
                                            <span className="text-gray-900 font-medium">
                                                {stats.blockedIP.blockedAttempts || 0}
                                            </span>
                                        </div>
                                        {stats.blockedIP.createdAt && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Fecha de bloqueo:</span>
                                                <span className="text-gray-900 font-medium">
                                                    {new Date(stats.blockedIP.createdAt).toLocaleString('es-ES')}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Información de Bloqueo en Memoria */}
                            {stats.memoryStats && (
                                <div className="border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <FontAwesomeIcon icon={faMemory} className="text-purple-600" />
                                        <h4 className="font-semibold text-gray-900">Bloqueo en Memoria</h4>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Estado:</span>
                                            <span className="text-gray-900 font-medium">
                                                {stats.memoryStats.blocked ? (
                                                    <span className="text-red-600">Bloqueada</span>
                                                ) : (
                                                    <span className="text-green-600">No bloqueada</span>
                                                )}
                                            </span>
                                        </div>
                                        {stats.memoryStats.blockedUntil && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Bloqueado hasta:</span>
                                                <span className="text-gray-900 font-medium">
                                                    {new Date(stats.memoryStats.blockedUntil).toLocaleString('es-ES')}
                                                </span>
                                            </div>
                                        )}
                                        {stats.memoryStats.reason && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Razón:</span>
                                                <span className="text-gray-900 font-medium">
                                                    {stats.memoryStats.reason}
                                                </span>
                                            </div>
                                        )}
                                        {stats.memoryStats.currentLimits && (
                                            <div className="mt-3 pt-3 border-t border-gray-200">
                                                <p className="text-gray-600 mb-2 font-medium">Límites Actuales:</p>
                                                <div className="space-y-1">
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Por minuto:</span>
                                                        <span className="text-gray-900">
                                                            {stats.memoryStats.currentLimits.minute || 0}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Por hora:</span>
                                                        <span className="text-gray-900">
                                                            {stats.memoryStats.currentLimits.hour || 0}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Por día:</span>
                                                        <span className="text-gray-900">
                                                            {stats.memoryStats.currentLimits.day || 0}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Info adicional */}
                            {!stats.blockedIP && !stats.memoryStats?.blocked && (
                                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                                    <div className="flex items-start gap-2">
                                        <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 mt-0.5" />
                                        <div className="text-sm text-blue-800">
                                            Esta IP no está bloqueada actualmente.
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default IPStatsModal;

